import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "../api";

const NAVY = "#1B1F5C";
export const WHATSAPP_NUMBER = "918828131962";
export const waLink = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

export const WhatsAppIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const inputCls = "w-full h-11 px-4 rounded-xl border border-gray-200 text-[13px] font-semibold outline-none focus:border-[#35C3C1] focus:ring-2 focus:ring-[#35C3C1]/20 bg-white";

const EnquiryWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-enquiry", handler);
    return () => window.removeEventListener("open-enquiry", handler);
  }, []);

  if (location.pathname === "/admin") return null;

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: "" }));
  };

  const validate = () => {
    const er = {};
    const name = form.name.trim();
    const phone = form.phone.replace(/[\s-]/g, "");
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name) er.name = "Please enter your name";
    else if (name.length < 2 || !/^[A-Za-z][A-Za-z.\s']*$/.test(name)) er.name = "Please enter a valid name";
    if (!phone) er.phone = "Please enter your phone number";
    else if (!/^(\+91|91|0)?[6-9]\d{9}$/.test(phone)) er.phone = "Enter a valid 10-digit Indian mobile number";
    if (email && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)) er.email = "Please enter a valid email address";
    if (!message) er.message = "Please write a short message";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await submitEnquiry({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Thank you! We'll get back to you shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
      setOpen(false);
    } catch {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating WhatsApp button */}
      <a
        href={waLink("Hi! I'd like to enquire about toys at Adhira & Appa Toys.")}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-btn"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-5 right-5 z-[70] h-14 w-14 rounded-full bg-[#25D366] text-white shadow-2xl flex items-center justify-center hover:scale-110 transition-transform"
      >
        <WhatsAppIcon size={30} />
      </a>

      {/* Enquiry modal */}
      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" data-testid="enquiry-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 md:p-8 text-left">
            <button
              onClick={() => setOpen(false)}
              data-testid="enquiry-modal-close"
              className="absolute top-4 right-4 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:text-[#F0426A] transition-colors"
              aria-label="Close enquiry form"
            >
              <X size={15} />
            </button>
            <h2 className="font-display font-extrabold text-xl md:text-2xl" style={{ color: NAVY }}>Send us an Enquiry</h2>
            <p className="mt-1 text-[12.5px] font-semibold text-gray-500">
              Tell us what you're looking for — we'll get back to you soon!
            </p>
            <form onSubmit={submit} noValidate className="mt-5 space-y-3">
              <div>
                <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name *" data-testid="enquiry-name-input" />
                {errors.name && <p className="mt-1 text-[11px] font-bold text-[#F0426A]" data-testid="enquiry-name-error">{errors.name}</p>}
              </div>
              <div>
                <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Phone number *" data-testid="enquiry-phone-input" />
                {errors.phone && <p className="mt-1 text-[11px] font-bold text-[#F0426A]" data-testid="enquiry-phone-error">{errors.phone}</p>}
              </div>
              <div>
                <input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="Email (optional)" data-testid="enquiry-email-input" />
                {errors.email && <p className="mt-1 text-[11px] font-bold text-[#F0426A]" data-testid="enquiry-email-error">{errors.email}</p>}
              </div>
              <div>
                <textarea
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[13px] font-semibold outline-none focus:border-[#35C3C1] focus:ring-2 focus:ring-[#35C3C1]/20 resize-none"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="What would you like to enquire about? *"
                  data-testid="enquiry-message-input"
                />
                {errors.message && <p className="mt-1 text-[11px] font-bold text-[#F0426A]" data-testid="enquiry-message-error">{errors.message}</p>}
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit-btn"
                className="w-full h-12 rounded-full text-white text-[12.5px] font-extrabold tracking-widest shadow-md hover:shadow-lg transition-shadow disabled:opacity-60 inline-flex items-center justify-center gap-2"
                style={{ backgroundColor: NAVY }}
              >
                <Send size={15} /> {sending ? "SENDING..." : "SEND ENQUIRY"}
              </button>
            </form>
            <a
              href={waLink("Hi! I'd like to enquire about toys at Adhira & Appa Toys.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enquiry-whatsapp-link"
              className="mt-3 w-full h-12 rounded-full inline-flex items-center justify-center gap-2 text-white text-[12.5px] font-extrabold tracking-widest bg-[#25D366] hover:brightness-105 transition-all"
            >
              <WhatsAppIcon size={18} /> CHAT ON WHATSAPP
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default EnquiryWidget;
